import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Label } from '../components/ui/label';
import { Clock, Loader2, Mail, Lock, User, AlertCircle, Gift, Building2 } from 'lucide-react';

const BUSINESS_CATEGORIES = [
  'Restaurant / Food',
  'Retail',
  'Professional Services',
  'Health & Wellness',
  'Trades & Repair',
  'Beauty & Personal Care',
  'Arts & Media',
  'Technology',
  'Education / Coaching',
  'Nonprofit / Community',
  'Other',
];

export function RegisterPage() {
  const [accountType, setAccountType] = useState('individual');
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessCategory, setBusinessCategory] = useState(BUSINESS_CATEGORIES[0]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (accountType === 'business' && !businessName.trim()) {
      setError('Business name is required');
      return;
    }

    setLoading(true);
    const extras = accountType === 'business' ? {
      account_type: 'business',
      business_name: businessName.trim(),
      business_category: businessCategory,
    } : { account_type: 'individual' };
    const result = await register(email, password, name, extras);
    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center" data-testid="register-logo">
          <div className="w-12 h-12 bg-[#ffd703] border-3 border-black flex items-center justify-center">
            <Clock className="w-7 h-7 text-black" />
          </div>
          <span className="font-bold text-2xl text-black font-sans">New Community Wealth Incubator</span>
        </Link>

        <div className="retro-card relative">
          {/* Window header */}
          <div className="retro-card-header">
            <div className="window-dots">
              <div className="window-dot window-dot-1"></div>
              <div className="window-dot window-dot-2"></div>
              <div className="window-dot window-dot-3"></div>
            </div>
            <span className="sparkle animate-sparkle">✦</span>
            <span className="font-sans font-semibold text-black">join us</span>
          </div>
          
          <div className="p-6">
            <h1 className="text-2xl font-bold text-black mb-2">Create an account</h1>
            <p className="text-black/60 font-sans text-sm mb-4">
              Join our community and start exchanging time
            </p>

            <div className="mb-4 p-3 rounded-md bg-[#93c5fd]/30 border-2 border-black flex items-start gap-2">
              <Gift className="w-5 h-5 text-black flex-shrink-0 mt-0.5" />
              <p className="text-sm text-black font-sans">New members receive <strong>3 bonus time credits</strong> to get started!</p>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-md bg-red-100 border-2 border-black flex items-start gap-2" data-testid="register-error">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600 font-sans">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Account type toggle */}
              <div className="space-y-2">
                <Label className="font-sans font-semibold">I'm registering as</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="account-type-toggle">
                  <button
                    type="button"
                    onClick={() => setAccountType('individual')}
                    className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 border-2 border-black font-sans font-semibold text-sm transition-all ${accountType === 'individual' ? 'bg-[#ffd703] shadow-[3px_3px_0px_#000]' : 'bg-white hover:bg-[#fef9e7]'}`}
                    data-testid="account-type-individual"
                  >
                    <User className="w-4 h-4" /> Individual
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountType('business')}
                    className={`flex items-center justify-center gap-2 rounded-md px-3 py-2 border-2 border-black font-sans font-semibold text-sm transition-all ${accountType === 'business' ? 'bg-[#ffd703] shadow-[3px_3px_0px_#000]' : 'bg-white hover:bg-[#fef9e7]'}`}
                    data-testid="account-type-business"
                  >
                    <Building2 className="w-4 h-4" /> Small Business
                  </button>
                </div>
              </div>

              {accountType === 'business' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="business_name" className="font-sans font-semibold">Business Name</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/50" />
                      <input
                        id="business_name"
                        type="text"
                        placeholder="Corner Bakery"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        className="retro-input w-full pl-10 pr-4 py-2 font-sans"
                        required
                        data-testid="register-business-name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="business_category" className="font-sans font-semibold">Business Category</Label>
                    <select
                      id="business_category"
                      value={businessCategory}
                      onChange={(e) => setBusinessCategory(e.target.value)}
                      className="retro-input w-full px-3 py-2 font-sans h-10"
                      data-testid="register-business-category"
                    >
                      {BUSINESS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="name" className="font-sans font-semibold">{accountType === 'business' ? 'Contact Person Name' : 'Full Name'}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/50" />
                  <input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="retro-input w-full pl-10 pr-4 py-2 font-sans"
                    required
                    data-testid="register-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="font-sans font-semibold">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/50" />
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="retro-input w-full pl-10 pr-4 py-2 font-sans"
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-sans font-semibold">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/50" />
                  <input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="retro-input w-full pl-10 pr-4 py-2 font-sans"
                    required
                    data-testid="register-password"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="font-sans font-semibold">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/50" />
                  <input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="retro-input w-full pl-10 pr-4 py-2 font-sans"
                    required
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              <button 
                type="submit" 
                className="retro-btn w-full py-3 font-sans"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div className="relative my-6">
              <div className="border-t-2 border-black"></div>
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#fef9e7] px-3 text-xs text-black/50 font-sans font-semibold">
                OR
              </span>
            </div>

            <button
              type="button"
              className="retro-btn retro-btn-outline w-full py-3 font-sans flex items-center justify-center gap-2"
              onClick={googleLogin}
              data-testid="register-google"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <p className="text-center text-sm text-black/60 mt-6 font-sans">
              Already have an account?{' '}
              <Link to="/login" className="text-[#e056a0] hover:underline font-semibold" data-testid="register-login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
